<template>
  <v-dialog
      v-model="dialogVisible"
      max-width="940"
      persistent
  >
    <v-card class="contatos-card">
      <div class="contatos-header">
        <div>
          <h3 class="title">Contatos de fornecedores</h3>
          <p class="subtitle">
            Defina os contatos de cada fornecedor que receberá as informações dessa cotação.
          </p>
        </div>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div v-if="fornecedorAtual" class="fornecedor-meta">
        <div class="meta-grid">
          <div>
            <p class="meta-label">CNPJ</p>
            <p class="meta-value">{{ formatCNPJ(fornecedorAtual.cnpj) }}</p>
          </div>
          <div>
            <p class="meta-label">Razão social | Nome fantasia</p>
            <p class="meta-value">
              {{ fornecedorAtual.razao_social || '—' }} | {{ fornecedorAtual.nome_fantasia || '—' }}
            </p>
          </div>
          <div>
            <p class="meta-label">Código fornecedor</p>
            <p class="meta-value">{{ fornecedorAtual.cod_fornecedor || '—' }}</p>
          </div>
        </div>

        <div class="navegacao" v-if="fornecedoresFiltrados.length > 1">
          <v-btn icon :disabled="!hasPrev" @click="prevFornecedor">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span>{{ currentIndex + 1 }} de {{ fornecedoresFiltrados.length }}</span>
          <v-btn icon :disabled="!hasNext" @click="nextFornecedor">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-if="fornecedorAtual && contatos.length > 0" class="selecao-contatos-banner">
        <p class="selecao-texto">
          Selecione <strong>apenas um</strong> contato deste fornecedor para representá-lo nesta cotação.
        </p>
        <div
          class="contatos-selecionados-badge"
          :class="{
            'has-selection': contatosSelecionadosCount > 0,
            'no-selection': contatosSelecionadosCount === 0
          }"
        >
          {{ contatosSelecionadosCount }} {{ contatosSelecionadosCount === 1 ? 'contato selecionado' : 'contatos selecionados' }}
        </div>
      </div>

      <div class="contatos-table">
        <v-simple-table class="simple-table">
          <thead>
          <tr>
            <th class="text-left" width="50" aria-hidden="true"></th>
            <th class="text-left" width="200">Contato</th>
            <th class="text-left">Cargo</th>
            <th class="text-left">WhatsApp</th>
            <th class="text-left">E-mail</th>
            <th class="text-left" width="120">Ações</th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="contatos.length === 0">
            <td colspan="6" class="text-center pa-6">
              Nenhum contato cadastrado para este fornecedor.
            </td>
          </tr>
          <tr
              v-for="contato in contatos"
              :key="contato.cotacao_representante_fornecedor_id || contato.email || contato.nome_representante"
              :class="{ 'contato-selecionado': isContatoSelecionado(contato) }"
          >
            <td>
              <v-checkbox
                  :input-value="isContatoSelecionado(contato)"
                  hide-details
                  dense
                  class="styled-checkbox"
                  :ripple="false"
                  @click.stop="toggleContato(contato)"
              ></v-checkbox>
            </td>
            <td>
              <div class="contato-cell">
                <div>
                  <p class="contato-nome">{{ contato.nome_representante || '—' }}</p>
                </div>
              </div>
            </td>
            <td>{{ contato.cargo_representante || '—' }}</td>
            <td>{{ contato.telefone_formatado || '—' }}</td>
            <td>{{ contato.email || '—' }}</td>
            <td>
              <v-btn icon small class="acao-btn" @click.stop="handleExcluirContato(contato)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
              <v-btn icon small class="acao-btn" @click.stop="handleEditarContato(contato)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </td>
          </tr>
          </tbody>
        </v-simple-table>

        <div class="adicionar-contato" @click="emitAdicionarContato">
          <div class="pill-icon">
            <v-icon small>mdi-plus</v-icon>
          </div>
          <span>Adicionar contato</span>
        </div>
      </div>

      <div class="contatos-footer">
        <div
          v-if="mostrarFiltroSemContato"
          class="ver-sem-contato"
        >
          <v-checkbox
              v-model="mostrarSomenteSemContato"
              hide-details
              dense
              class="styled-checkbox"
              :ripple="false"
          ></v-checkbox>
          <div>
            <p class="ver-title">Ver somente fornecedores sem contato.</p>
            <p class="ver-subtitle">Visualize apenas os fornecedores que ainda não possuem contatos cadastrados.</p>
          </div>
        </div>

        <v-spacer></v-spacer>

        <v-btn text class="wc-btn-actions-light" @click="closeDialog">
          Cancelar
        </v-btn>
        <v-btn color="primary" class="wc-btn-actions-light" @click="handleSalvar">
          Salvar
        </v-btn>
      </div>
    </v-card>
    <EditarContatoDialog
      :visible="editarContatoVisivel"
      :fornecedor="fornecedorAtual"
      :contato="contatoEditando"
      :handle-close="handleFecharEditarContato"
      @salvar="handleSalvarContatoEditado"
    />
    <NovoContatoDialog
      :visible="novoContatoVisivel"
      :fornecedor="fornecedorAtual"
      @salvar="handleSalvarNovoContato"
      @fechar="handleFecharNovoContato"
      @editar-existente="handleEditarContatoDuplicado"
    />
  </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits } from "vue";
import EditarContatoDialog from "@/components/webcotacao/components/EditarContatoDialog.vue";
import NovoContatoDialog from "@/components/webcotacao/components/NovoContatoDialog.vue";
import useRevisarContatosDialog from "@/components/webcotacao/composables/useRevisarContatosDialog";

const emit = defineEmits(["salvar", "adicionar-contato", "salvar-contato", "excluir-contato", "criar-contato"]);

const props = defineProps({
  visible: { type: Boolean, default: false },
  fornecedores: { type: Array, default: () => [] },
  initialIndex: { type: Number, default: 0 },
  handleClose: { type: Function },
  mostrarFiltroSemContato: { type: Boolean, default: false },
  idCotacao: { type: Number },
});

const {
  dialogVisible,
  mostrarSomenteSemContato,
  editarContatoVisivel,
  contatoEditando,
  novoContatoVisivel,
  fornecedoresFiltrados,
  fornecedorAtual,
  contatos,
  contatosSelecionadosCount,
  hasPrev,
  hasNext,
  prevFornecedor,
  nextFornecedor,
  formatCNPJ,
  isContatoSelecionado,
  toggleContato,
  emitAdicionarContato,
  handleEditarContato,
  handleFecharEditarContato,
  handleSalvarContatoEditado,
  handleExcluirContato,
  handleSalvar,
  handleSalvarNovoContato,
  handleFecharNovoContato,
  handleEditarContatoDuplicado,
  closeDialog,
} = useRevisarContatosDialog(props, emit);
</script>

<style scoped>
.v-dialog__content {
  align-items: flex-start;
}

.contatos-card {
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 32px 60px -28px rgba(9, 20, 49, 0.25);
}

.contatos-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #091431;
}

.subtitle {
  margin: 6px 0 0;
  color: #7081a1;
  font-size: 14px;
}

.fornecedor-meta {
  border: 1px solid #e0e6f2;
  border-radius: 16px;
  padding: 18px 20px;
  margin-bottom: 20px;
  background: #fdfdff;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.meta-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a0b8;
  margin-bottom: 4px;
}

.meta-value {
  font-size: 15px;
  color: #1c274c;
  margin: 0;
  font-weight: 500;
}

.navegacao {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #4a5875;
  font-weight: 500;
}

.navegacao .v-btn {
  border: 1px solid #d6ddef;
  border-radius: 10px;
  width: 38px;
  height: 38px;
}

.contatos-table {
  border: 1px solid #e0e6f2;
  border-radius: 16px;
  padding: 12px 16px 8px;
  margin-bottom: 20px;
  background: #fff;
}

:deep(.simple-table table) {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

:deep(.simple-table thead th) {
  background: #f4f7ff;
  color: #5e6a86;
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid #e5e9f4;
  padding: 12px 16px;
}

:deep(.simple-table tbody td) {
  padding: 14px 16px;
  color: #283046;
  font-size: 14px;
  border-bottom: 1px solid #eef1f7;
}

:deep(.simple-table tbody tr:last-child td) {
  border-bottom: none;
}

.contato-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.styled-checkbox {
  margin: 0;
}

:deep(.styled-checkbox .v-input--selection-controls__ripple) {
  display: none;
}

:deep(.styled-checkbox .v-label) {
  display: none;
}

.styled-checkbox-header {
  margin: 0;
}

:deep(.styled-checkbox-header .v-input--selection-controls__ripple) {
  display: none;
}

:deep(.styled-checkbox-header .v-label) {
  display: none;
}

.contato-nome {
  margin: 0;
  color: #111b3f;
}

.contato-meta {
  margin: 2px 0 0;
  font-size: 12px;
  color: #7c89a8;
}

.acao-btn {
  border: 1px solid #e2e7f3;
  border-radius: 10px;
  width: 36px;
  height: 36px;
}

.acao-btn + .acao-btn {
  margin-left: 8px;
}

.adicionar-contato {
  display: inline-flex;
  align-items: center;
  color: #4075ff;
  font-weight: 600;
  cursor: pointer;
  gap: 10px;
  margin: 12px 0 4px;
  font-size: 14px;
}

.adicionar-contato .pill-icon {
  width: 28px;
  height: 28px;
  border-radius: 14px;
  background: #ecf1ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.adicionar-contato:hover span {
  text-decoration: underline;
}

.contatos-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #edf0f7;
}

.ver-sem-contato {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ver-title {
  margin: 0;
  font-weight: 600;
  color: #1a2547;
}

.ver-subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: #7483a5;
}


.selecao-contatos-banner {
  background: transparent;
  border-radius: 12px;
  padding: 16px 0;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.selecao-texto {
  margin: 0;
  font-size: 14px;
  color: #5e6a86;
  font-weight: 400;
}

.contatos-selecionados-badge {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.contatos-selecionados-badge.has-selection {
  background: #E6FBEB;
  color: #0CB935;
}

.contatos-selecionados-badge.no-selection {
  background: #ffebee;
  color: #C81E1E;
}

.contato-selecionado {
  background-color: #f2f6fc !important;
}

.contato-selecionado td {
  background-color: #f2f6fc !important;
}
</style>
